import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';

interface Thread {
  id: string;
  title: string;
  conversationId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ThreadSidebarProps {
  conversationId: string | null;
  activeThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  onThreadCreated?: (thread: Thread) => void;
  onConversationDeleted?: () => void;
}

// Generate consistent colors based on thread title/id
const getThreadColors = (
  threadId: string,
  _index: number,
): { bg: string; border: string; text: string } => {
  const colors = [
    { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-500' },
    { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-500' },
    { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-500' },
    { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-500' },
    { bg: 'bg-yellow-500', border: 'border-yellow-500', text: 'text-yellow-500' },
    { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-500' },
    { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-500' },
    { bg: 'bg-indigo-500', border: 'border-indigo-500', text: 'text-indigo-500' },
    { bg: 'bg-teal-500', border: 'border-teal-500', text: 'text-teal-500' },
    { bg: 'bg-cyan-500', border: 'border-cyan-500', text: 'text-cyan-500' },
  ];

  // Use a simple hash of the threadId to get a consistent color
  let hash = 0;
  for (let i = 0; i < threadId.length; i++) {
    hash = (hash << 5) - hash + threadId.charCodeAt(i);
    hash = hash & hash;
  }
  return colors[Math.abs(hash) % colors.length];
};

export const ThreadSidebar: React.FC<ThreadSidebarProps> = ({
  conversationId,
  activeThreadId,
  onThreadSelect,
  onThreadCreated,
  onConversationDeleted,
}) => {
  const { t } = useTranslation();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [isLoading, setIsLoading] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const newThreadInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Load threads when conversation changes
  useEffect(() => {
    if (conversationId) {
      loadThreads();
    } else {
      setThreads([]);
    }
  }, [conversationId]);

  // Focus input when editing
  useEffect(() => {
    if (editingThreadId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingThreadId]);

  // Close expanded panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const loadThreads = async () => {
    if (!conversationId) return;

    setIsLoading(true);
    try {
      const response = await axios.get(`/chat/conversations/${conversationId}/threads`);
      setThreads(response.data);

      // If no active thread selected, select the first one (General)
      if (!activeThreadId && response.data.length > 0) {
        onThreadSelect(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load threads:', error);
      toast.error(t('threads.loadError', 'Failed to load threads'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newThreadTitle.trim()) return;

    if (!conversationId) {
      toast.error(
        t('threads.noConversation', 'No active conversation. Please start a chat first.'),
      );
      return;
    }

    try {
      const response = await axios.post(`/chat/conversations/${conversationId}/threads`, {
        title: newThreadTitle.trim(),
      });
      const newThread = response.data;
      setThreads((prev) => [...prev, newThread]);
      setNewThreadTitle('');
      onThreadSelect(newThread.id);
      onThreadCreated?.(newThread);
      setIsExpanded(false); // Auto-collapse after creating
      toast.success(t('threads.createSuccess', 'Thread created'));
    } catch (error) {
      console.error('Failed to create thread:', error);
      toast.error(t('threads.createError', 'Failed to create thread'));
    }
  };

  // Handle thread selection with auto-collapse
  const handleThreadSelectWithCollapse = (threadId: string) => {
    onThreadSelect(threadId);
    setIsExpanded(false); // Auto-collapse after selection
  };

  const handleRenameThread = async (threadId: string) => {
    if (!editingTitle.trim()) return;

    try {
      const response = await axios.patch(`/chat/threads/${threadId}`, {
        title: editingTitle.trim(),
      });
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, title: response.data.title } : t)),
      );
      setEditingThreadId(null);
      setEditingTitle('');
      toast.success(t('threads.renameSuccess', 'Thread renamed'));
    } catch (error) {
      console.error('Failed to rename thread:', error);
      toast.error(t('threads.renameError', 'Failed to rename thread'));
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      const response = await axios.delete(`/chat/threads/${threadId}`);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      setDeleteConfirmId(null);

      if (response.data.conversationDeleted) {
        toast.success(t('threads.deleteSuccess', 'Thread deleted'));
        onConversationDeleted?.();
        return;
      }

      // If deleted thread was active, switch to the most recent remaining thread
      if (activeThreadId === threadId) {
        const remainingThreads = threads.filter((t) => t.id !== threadId);
        if (remainingThreads.length > 0) {
          onThreadSelect(remainingThreads[0].id);
        }
      }
      toast.success(t('threads.deleteSuccess', 'Thread deleted'));
    } catch (error: any) {
      console.error('Failed to delete thread:', error);
      const message =
        error.response?.data?.message || t('threads.deleteError', 'Failed to delete thread');
      toast.error(message);
    }
  };

  const startEditing = (thread: Thread) => {
    setEditingThreadId(thread.id);
    setEditingTitle(thread.title);
  };

  const cancelEditing = () => {
    setEditingThreadId(null);
    setEditingTitle('');
  };

  return (
    <div className="relative h-full" ref={sidebarRef}>
      {/* Collapsed view - hidden when expanded */}
      <div
        className={`w-14 bg-dark-card dark:bg-dark-card bg-light-surface border-r border-dark-border dark:border-dark-border border-light-border flex flex-col items-center py-3 h-full ${isExpanded ? 'invisible' : ''}`}
      >
        {/* Add new thread button */}
        <button
          onClick={() => {
            setIsExpanded(true);
            // Focus input after panel expands
            setTimeout(() => {
              newThreadInputRef.current?.focus();
            }, 100);
          }}
          className="w-10 h-10 rounded-full bg-primary-600 hover:bg-primary-500 flex items-center justify-center text-white transition-colors mb-3 shadow-lg"
          aria-label={t('threads.newThread', 'New thread')}
        >
          <PlusIcon className="w-5 h-5" />
        </button>

        {/* Thread icons */}
        <div className="flex-1 overflow-y-auto space-y-2 w-full px-2">
          {threads.map((thread, index) => {
            const colors = getThreadColors(thread.id, index);
            const isActive = activeThreadId === thread.id;
            return (
              <button
                key={thread.id}
                onClick={() => {
                  if (isActive) {
                    setIsExpanded(!isExpanded);
                  } else {
                    handleThreadSelectWithCollapse(thread.id);
                  }
                }}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all mx-auto ${
                  isActive
                    ? `${colors.bg} text-white ring-2 ring-white/50 ring-offset-2 ring-offset-dark-card dark:ring-offset-dark-card ring-offset-light-surface`
                    : `bg-transparent border-2 ${colors.border} ${colors.text} hover:opacity-80`
                }`}
                title={thread.title}
              >
                {thread.title.charAt(0).toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Expanded overlay panel */}
      {isExpanded && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
            onClick={() => setIsExpanded(false)}
          />
          <div
            className="fixed left-0 top-0 bottom-0 w-72 z-[9999] flex flex-col animate-slide-in"
            style={{
              background: 'linear-gradient(180deg, #1a1625 0%, #151221 100%)',
              borderRight: '1px solid rgba(139, 92, 246, 0.15)',
              boxShadow: '4px 0 32px rgba(0,0,0,0.6)',
            }}
          >
            {/* Header */}
            <div className="p-3 border-b border-dark-border dark:border-dark-border border-light-border flex items-center justify-between">
              <span className="text-sm font-medium text-dark-text dark:text-dark-text text-light-text">
                {t('threads.title', 'Threads')}
              </span>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1.5 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-dark-text dark:hover:text-dark-text hover:text-light-text transition-colors"
                aria-label={t('threads.collapse', 'Close')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Thread list */}
            <div className="flex-1 overflow-y-auto py-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : threads.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                    {t('threads.noThreads', 'No threads yet')}
                  </p>
                </div>
              ) : (
                <div className="space-y-1 px-2">
                  {threads.map((thread, index) => {
                    const colors = getThreadColors(thread.id, index);
                    const isActive = activeThreadId === thread.id;
                    return (
                      <div
                        key={thread.id}
                        className={`group relative rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary-600/20'
                            : 'hover:bg-dark-bg/50 dark:hover:bg-dark-bg/50 hover:bg-light-bg/50'
                        }`}
                      >
                        {editingThreadId === thread.id ? (
                          <div className="flex items-center p-2 space-x-2">
                            <input
                              ref={editInputRef}
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameThread(thread.id);
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              className="flex-1 px-2 py-1 text-sm bg-dark-bg dark:bg-dark-bg bg-light-bg border border-dark-border dark:border-dark-border border-light-border rounded text-dark-text dark:text-dark-text text-light-text focus:outline-none focus:ring-1 focus:ring-primary-500"
                              maxLength={100}
                            />
                            <button
                              onClick={() => handleRenameThread(thread.id)}
                              className="p-1 text-green-500 hover:text-green-400"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1 text-red-500 hover:text-red-400"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : deleteConfirmId === thread.id ? (
                          <div className="p-2">
                            <p className="text-xs text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
                              {threads.length === 1
                                ? t(
                                    'threads.deleteLastConfirm',
                                    'This is the last thread. Deleting it will also delete the entire conversation. Continue?',
                                  )
                                : t('threads.deleteConfirm', 'Delete this thread?')}
                            </p>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDeleteThread(thread.id)}
                                className="flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                {t('common.delete', 'Delete')}
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="flex-1 px-2 py-1 text-xs bg-dark-bg dark:bg-dark-bg bg-light-bg text-dark-text dark:text-dark-text text-light-text rounded hover:bg-dark-border dark:hover:bg-dark-border hover:bg-light-border"
                              >
                                {t('common.cancel', 'Cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleThreadSelectWithCollapse(thread.id)}
                            className="w-full flex items-center p-2 text-left space-x-3"
                          >
                            {/* Thread color indicator */}
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                                isActive
                                  ? `${colors.bg} text-white`
                                  : `bg-transparent border-2 ${colors.border} ${colors.text}`
                              }`}
                            >
                              {thread.title.charAt(0).toUpperCase()}
                            </div>
                            <span
                              className={`text-sm truncate flex-1 ${
                                isActive
                                  ? 'text-primary-400 font-medium'
                                  : 'text-dark-text dark:text-dark-text text-light-text'
                              }`}
                            >
                              {thread.title}
                            </span>
                            <div className="hidden group-hover:flex items-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditing(thread);
                                }}
                                className="p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-500"
                              >
                                <PencilIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmId(thread.id);
                                }}
                                className="p-1 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary hover:text-red-500"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* New thread input - always visible */}
              <div className="px-2 py-2">
                <div className="flex items-center space-x-2">
                  <input
                    ref={newThreadInputRef}
                    type="text"
                    value={newThreadTitle}
                    onChange={(e) => setNewThreadTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateThread();
                    }}
                    placeholder={t('threads.newThread', 'New Thread')}
                    className="flex-1 px-2 py-1.5 text-sm bg-dark-bg dark:bg-dark-bg bg-light-bg border border-dark-border dark:border-dark-border border-light-border rounded text-dark-text dark:text-dark-text text-light-text placeholder-dark-text-secondary dark:placeholder-dark-text-secondary placeholder-light-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    maxLength={100}
                  />
                  <button
                    onClick={handleCreateThread}
                    disabled={!newThreadTitle.trim()}
                    className="p-1.5 text-primary-500 hover:text-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThreadSidebar;
